import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useRef } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useSocialMediaType } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import { useUpdateSocialAccounts } from '@/modules/statistics/social/hooks/use-update-social-account.tsx'
import { socialMessages } from '@/modules/statistics/social/types.ts'
import { ChipInput } from '@/modules/team/ui/chip-input'
import { handleError } from '@/utils/error'

interface AccountAddDialogProps {
  setOpen: Dispatch<SetStateAction<boolean>>
  open: boolean
}

export type AddSocialFormValues = z.infer<typeof AddSocialFormSchema>

const AddSocialFormSchema = z.object({
  username: z.array(z.string()),
})

const addAccountFormValues = {
  username: [],
}

export const AccountAddDialog = ({ setOpen, open }: AccountAddDialogProps) => {
  const { socialMediaType } = useSocialMediaType()
  const { addAccount } = useUpdateSocialAccounts()

  const toast = useToast()

  const chipInputRef = useRef<HTMLInputElement | null>(null)

  const addForm = useForm<AddSocialFormValues>({
    defaultValues: addAccountFormValues,
    resolver: zodResolver(AddSocialFormSchema),
  })

  const { handleSubmit, reset, control } = addForm

  const socialDisplayName = socialMessages[socialMediaType].socialDisplayName

  const closeModal = useCallback(() => {
    setOpen(false)
    reset(addAccountFormValues)
  }, [reset, setOpen])

  const onSubmit = useCallback(
    async (formValues: AddSocialFormValues) => {
      const unconfirmedAccounts = chipInputRef.current?.value

      if (
        !unconfirmedAccounts &&
        formValues.username &&
        formValues.username.length === 0
      ) {
        return
      }

      const mutations = formValues.username.map((user) =>
        addAccount.mutateAsync(user),
      )

      // Still add the last entered value as an account if it hasn't been confirmed as a chip yet
      if (unconfirmedAccounts) {
        mutations.push(addAccount.mutateAsync(unconfirmedAccounts))
      }

      await Promise.all(mutations)
        .then(() => {
          toast.success(`Successful added ${socialDisplayName} accounts`)
        })
        .catch((error) => {
          handleError(
            error,
            `Something went wrong adding the ${socialDisplayName} accounts`,
          )
        })

      reset(addAccountFormValues)
      closeModal()
    },
    [addAccount, closeModal, reset, socialDisplayName, toast],
  )

  return (
    <Dialog
      show={open}
      onClose={closeModal}
      bigDialog
    >
      <Dialog.Header>
        <Dialog.Title className="text-center">
          Add {socialDisplayName} Account
        </Dialog.Title>
        <Dialog.Description className="text-center">
          Only add {socialDisplayName} accounts that match the creator
        </Dialog.Description>
      </Dialog.Header>
      <FormProvider {...addForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="my-5">
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <ChipInput
                  {...field}
                  ref={chipInputRef}
                  onChange={field.onChange}
                  placeholder={`${socialDisplayName} Name, comma or space separated`}
                />
              )}
            />
          </div>

          <Dialog.ClosableFooter onClose={closeModal}>
            <Button
              type="submit"
              variant="default"
              isLoading={addAccount.isLoading}
            >
              Add Account
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
